import React from 'react';
import {useTranslation} from 'react-i18next';
import {cn} from '@chekinapp/ui';
import {PropertyLinkPhoto, useArrivalProperty} from 'components/arrival';
import {useReservation} from 'context/reservation';
import {useAppSurfaceSettled} from 'components/SplashScreen/useAppSurfaceSettled';
import {useSplashController} from 'components/SplashScreen/useSplashController';
import {useSplashCoverStore} from 'components/SplashScreen/useSplashCoverStore';
import {SplashStatus} from 'components/SplashScreen/SplashStatus';
import {DARK_INK_VARS} from 'components/SplashScreen/splashInk';
import {SPLASH_TIMING, getAnnounceKey} from 'components/SplashScreen/constants';
import {BOOKING_CARD_REVEAL_MS} from './bookingCardTiming';
import {EntranceBookingCard} from './EntranceBookingCard';
import {EntranceReveal} from './EntranceReveal';
import {ENTRANCE_EXIT_MS, playEntranceExit} from './entranceExit';
import type {EntranceKind} from './useEntranceKind';
import styles from './entrance.module.css';

const NAME_HOLD_MS = 1600;
const COVER_MS = 250;
const BOOKING_CARD_READ_MS = 600;

type Props = {
  kind: EntranceKind;
  isDataReady: boolean;
  shouldDismiss: boolean;
  onCovered: () => void;
  onRetry: () => void;
};

function EntranceScreen({kind, isDataReady, shouldDismiss, onCovered, onRetry}: Props) {
  const {t} = useTranslation();
  const property = useArrivalProperty();
  const {isLoading: isReservationLoading} = useReservation();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [startedAt] = React.useState(Date.now);

  const isBookingCard = kind !== 'property';
  const isContentReady =
    isDataReady && (isBookingCard ? !isReservationLoading : !property.isLoading);
  const isSurfaceSettled = useAppSurfaceSettled(isContentReady, rootRef);
  const [isNameHeld, setIsNameHeld] = React.useState(false);

  React.useEffect(
    function holdTheNameOnScreen() {
      if (!isContentReady || isNameHeld) {
        return;
      }

      const revealLeftMs = isBookingCard
        ? startedAt + BOOKING_CARD_REVEAL_MS + BOOKING_CARD_READ_MS - Date.now()
        : 0;
      const timeout = setTimeout(
        () => setIsNameHeld(true),
        Math.max(NAME_HOLD_MS, revealLeftMs),
      );
      return () => clearTimeout(timeout);
    },
    [isContentReady, isNameHeld, isBookingCard, startedAt],
  );

  const {phase, exit, stage, elapsedMs, restart} = useSplashController({
    isReady: isContentReady && isSurfaceSettled && isNameHeld,
    shouldDismiss,
    closingMs: ENTRANCE_EXIT_MS[kind],
  });

  React.useEffect(
    function reportCovered() {
      const timeout = setTimeout(onCovered, COVER_MS);
      return () => clearTimeout(timeout);
    },
    [onCovered],
  );

  React.useEffect(
    function playExitOnHandoff() {
      if (phase !== 'closing' || exit !== 'handoff' || !rootRef.current) {
        return;
      }

      playEntranceExit(kind, rootRef.current);
    },
    [phase, exit, kind],
  );

  const cover =
    phase === 'visible'
      ? 'covering'
      : phase === 'closing' && exit === 'handoff'
      ? 'revealing'
      : 'none';
  const setCover = useSplashCoverStore(state => state.setCover);
  React.useEffect(
    function reportCoverToStore() {
      setCover(cover);
      return () => setCover('none');
    },
    [cover, setCover],
  );

  const announceKey = getAnnounceKey(stage, phase !== 'visible');

  const handleRetry = () => {
    onRetry();
    restart();
  };

  if (phase === 'gone') {
    return null;
  }

  return (
    <div
      ref={rootRef}
      role="status"
      aria-live="polite"
      aria-busy={phase === 'visible'}
      className={cn(
        styles.root,
        isContentReady && styles.named,
        phase !== 'visible' && 'pointer-events-none',
        'transition-[opacity,visibility] ease-in-out motion-reduce:transition-none',
        phase === 'fading' && 'invisible opacity-0',
      )}
      style={
        {
          transitionDuration: `${SPLASH_TIMING.curtain}ms`,
          '--hold': `${NAME_HOLD_MS}ms`,
          ...DARK_INK_VARS,
        } as React.CSSProperties
      }
    >
      <div data-entrance="backdrop" className={styles.backdrop}>
        <PropertyLinkPhoto />
      </div>

      {kind === 'property' ? (
        <>
          <EntranceReveal startedAt={startedAt} />
          <i data-entrance="progress" className={styles.progress} />
        </>
      ) : (
        <EntranceBookingCard startedAt={startedAt} />
      )}

      {stage === 'slow' && phase === 'visible' && (
        <SplashStatus
          stage={stage}
          elapsedMs={elapsedMs}
          onRetry={handleRetry}
          className="absolute inset-x-0 bottom-10 z-[3] px-6"
        />
      )}

      <span className="sr-only">{announceKey ? t(announceKey) : null}</span>
    </div>
  );
}

export {EntranceScreen};
